import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute, { PublicOnlyRoute } from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './LandingPage';
import LoginScreen from './LoginScreen';
import AboutUs from './AboutUs';
import PricingPage from './PricingPage';
import SetupScreen from './SetupScreen';
import RealtimeConversation from './RealtimeConversation';
import Dashboard from './Dashboard';
import CompanyDashboard from './CompanyDashboard';
import CollegeDashboard from './CollegeDashboard';
import InstitutionalDashboard from './InstitutionalDashboard';
import InstMcqRoute from './InstMcqRoute';
import InstCodingRoute from './InstCodingRoute';
import PaymentPage from './PaymentPage';
import RecruiterJobsDashboard from './components/RecruiterJobsDashboard';
import JobInterviewRoute from './components/JobInterviewRoute';
import CandidateFormPage from './components/CandidateFormPage';
import CodingPractice from './components/CodingPractice';
import AssessmentScreen from './AssessmentScreen';
import PilotAnalytics from './PilotAnalytics';
import ReportPreviewPage from './ReportPreviewPage';
import LockedFeatureCard from './components/LockedFeatureCard';
import FastTrackUpsellModal from './components/FastTrackUpsellModal';
import useFreemiumLock from './hooks/useFreemiumLock';
import { isFreemiumEnabled } from './utils/freemiumFlag';
import { getFreemiumStatus } from './services/freemiumApi';

/**
 * App — React Router v6 routing replaces all boolean-flag navigation.
 *
 * Public routes:  /  /login  /about  /pricing
 * Protected:      /dashboard  /setup  /interview  /payment  /company  /college
 *
 * Auth state + interview state live in AuthContext.
 * ProtectedRoute enforces login + role checks via backendRedirectTo.
 */

// ============ Animated gradient blob background (shared) ============
function AppBackground() {
  return (
    <div className="app-bg-blobs" aria-hidden="true">
      <div className="app-blob app-blob-1" />
      <div className="app-blob app-blob-2" />
      <div className="app-blob app-blob-3" />
    </div>
  );
}

// ============ Shared header for authenticated pages ============
function AuthTopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enabled, isFreeTier, isExhausted, refresh } = useFreemiumLock();
  const [modalOpen, setModalOpen] = React.useState(false);

  const navItems = [
    { label: 'Dashboard', path: '/dashboard', match: ['/dashboard'], locked: false },
    { label: 'Start Interview', path: '/setup', match: ['/setup', '/interview'], locked: enabled && isExhausted },
    { label: 'Coding Practice', path: '/coding', match: ['/coding'], locked: enabled && isFreeTier },
    { label: 'Assessment Test', path: '/assessment', match: ['/assessment'], locked: enabled && isFreeTier },
  ];

  return (
    <nav className="app-main-nav" aria-label="Primary">
      {navItems.map((item) => {
        const active = item.match.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
        return (
          <button
            key={item.path}
            type="button"
            className={`app-main-nav__item${active ? ' active' : ''}`}
            onClick={() => (item.locked ? setModalOpen(true) : navigate(item.path))}
          >
            {item.locked && <span aria-hidden="true">🔒 </span>}
            {item.label}
          </button>
        );
      })}
      <FastTrackUpsellModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={refresh}
      />
    </nav>
  );
}

// branding (optional, per-job — see JobBrandingModal): { name, logo }, either
// may be empty. With neither (or a logo that fails to load and no name), the
// plain Vagmee header renders exactly as before — never an empty slot.
function AppHeader({ title = 'Vagmee', tagline = 'Practice smarter. Get hired faster.', actions, showMainNav = false, branding = null }) {
  const [brokenLogo, setBrokenLogo] = React.useState("");
  const brandName = (branding?.name || "").trim();
  const brandLogo = branding?.logo && branding.logo !== brokenLogo ? branding.logo : "";
  const coBranded = !!(brandName || brandLogo);

  return (
    <div className="app-header">
      <div className="app-header-top">
        {coBranded ? (
          <div className="app-brand-block">
            <div className="cobrand-row">
              {brandLogo && (
                <img src={brandLogo} alt={brandName || "Company logo"} className="cobrand-logo" onError={() => setBrokenLogo(brandLogo)} />
              )}
              {brandName && <h2 className="cobrand-name">{brandName}</h2>}
            </div>
            <div className="cobrand-powered">
              <span>powered by</span>
              <img src="/Vagmee_Logo.png" alt="" className="cobrand-vagmee-logo" />
              <strong>Vagmee</strong>
              <span className="cobrand-sep">·</span>
              <a href="https://www.vagmee.in" target="_blank" rel="noopener noreferrer">www.vagmee.in</a>
            </div>
          </div>
        ) : (
        <div className="app-brand-block">
          <div className="app-brand-row">
            <div className="logo-container small">
              <img src="/Vagmee_Logo.png" alt="Vagmee Logo" className="app-logo" />
            </div>
            <h2 className="app-title">{title}</h2>
          </div>
          <p className="app-tagline">{tagline}</p>
        </div>
        )}
        {showMainNav && <AuthTopNav />}
        {coBranded ? (
          // Co-branded: test name sits on the right, under the Logout action.
          <div className="cobrand-side">
            {actions}
            {tagline && <p className="app-tagline cobrand-test-name">{tagline}</p>}
          </div>
        ) : actions}
      </div>
    </div>
  );
}

// ============ Payment page wrapper ============
function PaymentPageWrapper() {
  const { handlePaymentBack, handlePaymentSuccess } = useAuth();
  return (
    <ErrorBoundary>
      <PaymentPage onBack={handlePaymentBack} onSuccess={handlePaymentSuccess} />
    </ErrorBoundary>
  );
}

// ============ Setup page wrapper ============
function SetupPageWrapper() {
  const { handleStartInterview, handleLogout } = useAuth();
  const { enabled, loading, isExhausted, refresh } = useFreemiumLock();
  const [modalOpen, setModalOpen] = React.useState(false);
  const locked = enabled && isExhausted;

  React.useEffect(() => {
    if (!loading && locked) setModalOpen(true);
  }, [loading, locked]);

  return (
    <div className="app-container recruiter-app-container">
      <AppBackground />
      <AppHeader
        showMainNav
        actions={
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        }
      />
      {enabled && loading ? null : locked ? (
        <LockedFeatureCard
          title="You've used your free interview"
          description="Unlock 5 more interviews, coding practice, MCQ tests, and full reports with Fast Track Practice."
          onUnlock={() => setModalOpen(true)}
        />
      ) : (
        <SetupScreen
          onStart={handleStartInterview}
          setupPayload={null}
          onBlocked={enabled ? () => setModalOpen(true) : undefined}
        />
      )}
      <FastTrackUpsellModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={refresh}
      />
    </div>
  );
}

// ============ Dashboard page wrapper ============
function DashboardPageWrapper() {
  const { user, handleLogout } = useAuth();
  const navigate = useNavigate();
  const { enabled, isExhausted, refresh } = useFreemiumLock();
  const [modalOpen, setModalOpen] = React.useState(false);

  const handleStartInterviewClick = () => {
    if (enabled && isExhausted) {
      setModalOpen(true);
      return;
    }
    navigate('/setup');
  };

  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        showMainNav
        actions={
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        }
      />
      <div className="dashboard-layout">
        <div className="dashboard-content">
          <ErrorBoundary>
            <Dashboard
              user={user}
              onStartInterview={handleStartInterviewClick}
              reportLocked={enabled && isExhausted}
              onLockedReportClick={() => setModalOpen(true)}
            />
          </ErrorBoundary>
        </div>
      </div>
      <FastTrackUpsellModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={refresh}
      />
    </div>
  );
}

// ============ Report preview page wrapper (Freemium Conversion Funnel) ============
function ReportPreviewPageWrapper() {
  const { handleLogout } = useAuth();
  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        actions={
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        }
      />
      <ErrorBoundary>
        <ReportPreviewPage />
      </ErrorBoundary>
    </div>
  );
}

// ============ Interview page wrapper ============
function InterviewPageWrapper() {
  const {
    user,
    apiBase,
    mountKey,
    sessionId,
    interviewConfig,
    setInterviewConfig,
    setSessionId,
    handleGoBack,
    handleLogout,
  } = useAuth();
  const navigate = useNavigate();

  const isInviteLinkedInterview = Boolean(interviewConfig?.invite_interview_id);

  const handleReturnToDashboard = React.useCallback(async () => {
    const completedSessionId = sessionId;
    setInterviewConfig(null);
    setSessionId(null);
    localStorage.removeItem('interviewConfig');
    sessionStorage.removeItem('mockbot_session_id');

    if (isFreemiumEnabled() && completedSessionId) {
      try {
        const status = await getFreemiumStatus();
        if (status?.is_free_tier) {
          navigate(`/report-preview/${completedSessionId}`);
          return;
        }
      } catch (err) {
        console.error('Freemium status check failed, falling back to dashboard:', err);
      }
    }

    navigate('/dashboard');
  }, [navigate, setInterviewConfig, setSessionId, sessionId]);

  // Guard: no config → redirect to dashboard
  if (!interviewConfig) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        showMainNav={!isInviteLinkedInterview}
        actions={
          <div style={{ display: 'flex', gap: '0.625rem', marginLeft: 'auto', marginRight: '1.875rem' }}>
            {!isInviteLinkedInterview && (
              <button onClick={handleGoBack} className="logout-button">
                Back
              </button>
            )}
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        }
      />
      <ErrorBoundary>
        <RealtimeConversation
          key={mountKey}
          apiBase={apiBase}
          interviewConfig={interviewConfig}
          onGoBack={isInviteLinkedInterview ? null : handleReturnToDashboard}
          user={user}
          sessionId={sessionId}
          onLogout={handleLogout}
        />
      </ErrorBoundary>
    </div>
  );
}

// ============ Coding practice wrapper ============
function CodingPracticePageWrapper() {
  const { handleLogout } = useAuth();
  const { enabled, loading, isFreeTier, refresh } = useFreemiumLock();
  const [modalOpen, setModalOpen] = React.useState(false);
  const locked = enabled && isFreeTier;

  React.useEffect(() => {
    if (!loading && locked) setModalOpen(true);
  }, [loading, locked]);

  return (
    <div className="app-container coding-app-container">
      <AppBackground />
      <AppHeader
        showMainNav
        actions={
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        }
      />
      <ErrorBoundary>
        {enabled && loading ? null : locked ? (
          <LockedFeatureCard
            title="Coding Practice is a Fast Track feature"
            description="Unlock coding practice, MCQ tests, 5 interviews, and full reports for ₹299 / 7 days."
            onUnlock={() => setModalOpen(true)}
          />
        ) : (
          <CodingPractice />
        )}
      </ErrorBoundary>
      <FastTrackUpsellModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={refresh}
      />
    </div>
  );
}

// ============ Assessment test wrapper ============
function AssessmentTestPageWrapper() {
  const { handleLogout } = useAuth();
  const { enabled, loading, isFreeTier, refresh } = useFreemiumLock();
  const [modalOpen, setModalOpen] = React.useState(false);
  const locked = enabled && isFreeTier;

  React.useEffect(() => {
    if (!loading && locked) setModalOpen(true);
  }, [loading, locked]);

  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        showMainNav
        actions={
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        }
      />
      <div className="assessment-page-body">
        <ErrorBoundary>
          {enabled && loading ? null : locked ? (
            <LockedFeatureCard
              title="MCQ Tests are a Fast Track feature"
              description="Unlock MCQ tests, coding practice, 5 interviews, and full reports for ₹299 / 7 days."
              onUnlock={() => setModalOpen(true)}
            />
          ) : (
            <AssessmentScreen />
          )}
        </ErrorBoundary>
      </div>
      <FastTrackUpsellModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={refresh}
      />
    </div>
  );
}

// ============ Company dashboard wrapper ============
function CompanyPageWrapper() {
  const { user, handleLogout } = useAuth();
  const navigate = useNavigate();
  const userRole = user?.role || 'COMPANY';

  if (userRole === 'COMPANY' || userRole === 'COMPANY_HR') {
    return (
      <div className="app-container recruiter-app-container">
        <AppBackground />
        <AppHeader
          title="Vagmee - Hiring Dashboard"
          tagline="Company dashboard for roles, candidates, recordings, and reports"
          actions={
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          }
        />
        <ErrorBoundary>
          <RecruiterJobsDashboard user={user} />
        </ErrorBoundary>
      </div>
    );
  }

  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        title="Vagmee - Hiring Dashboard"
        tagline="Review candidate interview results"
        actions={
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        }
      />
      <CompanyDashboard user={user} />
    </div>
  );
}

// ============ Recruiter job management wrapper ============
function RecruiterJobsPageWrapper() {
  const { user, handleLogout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        title="Vagmee - Recruiter"
        tagline="Create and manage candidate interview links"
        actions={
          <div style={{ display: 'flex', gap: '0.625rem', marginLeft: 'auto', marginRight: '1.875rem' }}>
            <button onClick={() => navigate('/company')} className="logout-button">
              Results
            </button>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        }
      />
      <ErrorBoundary>
        <RecruiterJobsDashboard user={user} />
      </ErrorBoundary>
    </div>
  );
}

// ============ Institutional dashboard wrapper ============
function CollegePageWrapper() {
  const { user, handleLogout } = useAuth();
  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        title="Vagmee"
        tagline="Practice smarter. Get hired faster."
        actions={
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        }
      />
      <InstitutionalDashboard user={user} />
    </div>
  );
}

// ============ Admin analytics wrapper ============
function AdminPageWrapper() {
  const { handleLogout } = useAuth();
  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        title="Vagmee"
        tagline="Pilot Analytics & Cost Monitor"
        actions={<button onClick={handleLogout} className="logout-button">Logout</button>}
      />
      <PilotAnalytics />
    </div>
  );
}

// ============ Route definitions ============
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PublicOnlyRoute><LandingPage /></PublicOnlyRoute>} />
      <Route path="/login" element={<PublicOnlyRoute><LoginScreen /></PublicOnlyRoute>} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/pricing" element={<PricingPage />} />

      {/* Protected routes */}
      <Route path="/payment" element={<ProtectedRoute allowedRedirects={['PAYMENT']}><PaymentPageWrapper /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute allowedRedirects={['INTERVIEWEE_DASHBOARD', 'PAYMENT']}><SetupPageWrapper /></ProtectedRoute>} />
      <Route path="/interview" element={<ProtectedRoute allowedRedirects={['INTERVIEWEE_DASHBOARD', 'PAYMENT']}><InterviewPageWrapper /></ProtectedRoute>} />
      <Route path="/interview/:jobId" element={<JobInterviewRoute AppBackground={AppBackground} AppHeader={AppHeader} />} />
      <Route path="/form/:candidateId" element={<CandidateFormPage AppBackground={AppBackground} />} />
      <Route path="/dashboard" element={<ProtectedRoute allowedRedirects={['INTERVIEWEE_DASHBOARD']}><DashboardPageWrapper /></ProtectedRoute>} />
      <Route path="/coding" element={<ProtectedRoute allowedRedirects={['INTERVIEWEE_DASHBOARD']}><CodingPracticePageWrapper /></ProtectedRoute>} />
      <Route path="/coding/pattern/:patternId" element={<ProtectedRoute allowedRedirects={['INTERVIEWEE_DASHBOARD']}><CodingPracticePageWrapper /></ProtectedRoute>} />
      <Route path="/coding/problem/:problemId" element={<ProtectedRoute allowedRedirects={['INTERVIEWEE_DASHBOARD']}><CodingPracticePageWrapper /></ProtectedRoute>} />
      <Route path="/assessment" element={<ProtectedRoute allowedRedirects={['INTERVIEWEE_DASHBOARD']}><AssessmentTestPageWrapper /></ProtectedRoute>} />
      <Route path="/report-preview/:sessionId" element={<ProtectedRoute allowedRedirects={['INTERVIEWEE_DASHBOARD', 'PAYMENT']}><ReportPreviewPageWrapper /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute allowedRedirects={['COMPANY_DASHBOARD']}><CompanyPageWrapper /></ProtectedRoute>} />
      <Route path="/company/jobs" element={<ProtectedRoute allowedRedirects={['COMPANY_DASHBOARD']}><RecruiterJobsPageWrapper /></ProtectedRoute>} />
      <Route path="/college" element={<ProtectedRoute allowedRedirects={['COLLEGE_DASHBOARD']}><CollegePageWrapper /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<AdminPageWrapper />} />

      {/* Institutional campaign candidate routes (public — handle auth inline) */}
      <Route path="/inst/mcq/:campaignId" element={<><AppBackground /><InstMcqRoute /></>} />
      <Route path="/inst/coding/:campaignId" element={<InstCodingRoute />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ============ App root ============
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: { fontSize: '0.95rem', borderRadius: '8px', padding: '12px 16px' }
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
